import React, { useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (n) => `Rs ${Number(n || 0).toLocaleString()}`;

const ReportsTab = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    if (!startDate || !endDate) return alert('Please select start and end date');
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agriculture/reports?start_date=${startDate}&end_date=${endDate}`, { headers: headers() });
      setData(res.data.data);
    } catch { alert('Failed to fetch report'); }
    setLoading(false);
  };

  const generatePDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.width;

    // Header
    doc.setFillColor(39, 174, 96);
    doc.rect(0, 0, pageW, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22); doc.setFont(undefined, 'bold');
    doc.text('Sohaib Enterprises', pageW / 2, 16, { align: 'center' });
    doc.setFontSize(13); doc.setFont(undefined, 'normal');
    doc.text('Agriculture Module — Report', pageW / 2, 26, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Period: ${startDate} to ${endDate}`, pageW / 2, 35, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    let y = 50;

    // Summary
    const totalIncome = data.finance.filter(f => f.transaction_type === 'income').reduce((s, f) => s + parseFloat(f.amount), 0);
    const totalExpense = data.finance.filter(f => f.transaction_type === 'expense').reduce((s, f) => s + parseFloat(f.amount), 0);
    const totalLabor = data.labor.reduce((s, l) => s + parseFloat(l.total_amount || 0), 0);
    const totalSales = data.sales.reduce((s, s2) => s + parseFloat(s2.total_amount || 0), 0);

    doc.setFontSize(13); doc.setFont(undefined, 'bold');
    doc.text('Summary', 14, y); y += 8;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Total Crops', data.crops.length],
        ['Total Income', fmt(totalIncome)],
        ['Total Expenses', fmt(totalExpense)],
        ['Net Profit/Loss', fmt(totalIncome - totalExpense)],
        ['Labor Cost', fmt(totalLabor)],
        ['Harvest Sales', fmt(totalSales)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [39, 174, 96] },
      styles: { fontSize: 10 },
    });
    y = doc.lastAutoTable.finalY + 12;

    // Crops
    if (data.crops.length > 0) {
      doc.setFontSize(13); doc.setFont(undefined, 'bold');
      doc.text('Crops', 14, y); y += 8;
      autoTable(doc, {
        startY: y,
        head: [['Crop', 'Type', 'Field', 'Area (Acres)', 'Sowing Date', 'Status', 'Yield (kg)']],
        body: data.crops.map(c => [c.crop_name, c.crop_type || '—', c.field_name || '—', c.area_sown_acres, c.sowing_date?.split('T')[0] || '—', c.status, c.actual_yield_kg || c.expected_yield_kg || '—']),
        theme: 'striped',
        headStyles: { fillColor: [39, 174, 96] },
        styles: { fontSize: 9 },
      });
      y = doc.lastAutoTable.finalY + 12;
    }

    // Finance
    if (data.finance.length > 0) {
      doc.setFontSize(13); doc.setFont(undefined, 'bold');
      doc.text('Finance Transactions', 14, y); y += 8;
      autoTable(doc, {
        startY: y,
        head: [['Date', 'Type', 'Category', 'Description', 'Amount']],
        body: data.finance.map(f => [f.transaction_date?.split('T')[0], f.transaction_type, f.category, f.description || '—', fmt(f.amount)]),
        theme: 'striped',
        headStyles: { fillColor: [39, 174, 96] },
        styles: { fontSize: 9 },
      });
      y = doc.lastAutoTable.finalY + 12;
    }

    // Labor
    if (data.labor.length > 0) {
      doc.setFontSize(13); doc.setFont(undefined, 'bold');
      doc.text('Labor Records', 14, y); y += 8;
      autoTable(doc, {
        startY: y,
        head: [['Worker', 'Work Type', 'Date', 'Days', 'Daily Wage', 'Total']],
        body: data.labor.map(l => [l.worker_name, l.work_type, l.work_date?.split('T')[0], l.days_worked, fmt(l.daily_wage), fmt(l.total_amount)]),
        theme: 'striped',
        headStyles: { fillColor: [39, 174, 96] },
        styles: { fontSize: 9 },
      });
      y = doc.lastAutoTable.finalY + 12;
    }

    // Harvest Sales
    if (data.sales.length > 0) {
      doc.setFontSize(13); doc.setFont(undefined, 'bold');
      doc.text('Harvest Sales', 14, y); y += 8;
      autoTable(doc, {
        startY: y,
        head: [['Crop', 'Buyer', 'Qty (kg)', 'Price/kg', 'Total', 'Date', 'Status']],
        body: data.sales.map(s => [s.crop_name || '—', s.buyer_name || '—', s.quantity_kg, fmt(s.price_per_kg), fmt(s.total_amount), s.sale_date?.split('T')[0], s.payment_status]),
        theme: 'striped',
        headStyles: { fillColor: [39, 174, 96] },
        styles: { fontSize: 9 },
      });
    }

    // Footer
    doc.setFontSize(9); doc.setTextColor(150);
    doc.text(`Generated on ${new Date().toLocaleDateString('en-PK')} | Sohaib Enterprises - Agriculture`, pageW / 2, doc.internal.pageSize.height - 10, { align: 'center' });

    doc.save(`Agriculture-Report-${startDate}-to-${endDate}.pdf`);
  };

  return (
    <div>
      <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', marginBottom: '24px' }}>Reports</h2>

      {/* Date Selector */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '28px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '20px' }}>Select Date Range</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' }}>Start Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' }}>End Date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <button onClick={fetchReport} disabled={loading} style={{ padding: '10px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>
            {loading ? 'Loading...' : '🔍 Generate Report'}
          </button>
        </div>
      </div>

      {/* Report Preview */}
      {data && (
        <div>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            {[
              { label: 'Crops', value: data.crops.length, color: '#27ae60', icon: '🌾' },
              { label: 'Finance Entries', value: data.finance.length, color: '#3498db', icon: '💰' },
              { label: 'Labor Records', value: data.labor.length, color: '#f39c12', icon: '👷' },
              { label: 'Harvest Sales', value: data.sales.length, color: '#9b59b6', icon: '🛒' },
              { label: 'Inventory Items', value: data.inventory.length, color: '#1abc9c', icon: '🏪' },
            ].map(c => (
              <div key={c.label} style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `4px solid ${c.color}`, textAlign: 'center' }}>
                <div style={{ fontSize: '24px' }}>{c.icon}</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: c.color }}>{c.value}</div>
                <div style={{ fontSize: '13px', color: '#7f8c8d' }}>{c.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button onClick={generatePDF} style={{ padding: '12px 28px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>
              📄 Download PDF Report
            </button>
          </div>

          {/* Crops Table */}
          {data.crops.length > 0 && (
            <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: '#2c3e50' }}>🌾 Crops ({data.crops.length})</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#f8f9fa' }}>
                  {['Crop', 'Field', 'Area', 'Sowing Date', 'Status', 'Yield (kg)'].map(h => <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '13px', color: '#555', borderBottom: '2px solid #eee' }}>{h}</th>)}
                </tr></thead>
                <tbody>{data.crops.map((c, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 14px', fontWeight: '600' }}>{c.crop_name}</td>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{c.field_name || '—'}</td>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{c.area_sown_acres} acres</td>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{c.sowing_date?.split('T')[0]}</td>
                    <td style={{ padding: '10px 14px' }}><span style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '12px', background: '#27ae6020', color: '#27ae60' }}>{c.status}</span></td>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{c.actual_yield_kg || c.expected_yield_kg || '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {/* Finance Table */}
          {data.finance.length > 0 && (
            <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: '#2c3e50' }}>💰 Finance ({data.finance.length})</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#f8f9fa' }}>
                  {['Date', 'Type', 'Category', 'Amount'].map(h => <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '13px', color: '#555', borderBottom: '2px solid #eee' }}>{h}</th>)}
                </tr></thead>
                <tbody>{data.finance.map((f, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{f.transaction_date?.split('T')[0]}</td>
                    <td style={{ padding: '10px 14px' }}><span style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '12px', background: f.transaction_type === 'income' ? '#27ae6020' : '#e74c3c20', color: f.transaction_type === 'income' ? '#27ae60' : '#e74c3c' }}>{f.transaction_type}</span></td>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{f.category}</td>
                    <td style={{ padding: '10px 14px', fontWeight: '700', color: f.transaction_type === 'income' ? '#27ae60' : '#e74c3c' }}>{fmt(f.amount)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {data.crops.length === 0 && data.finance.length === 0 && data.labor.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d', background: 'white', borderRadius: '16px' }}>No data found for selected date range.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsTab;